/**
 * Common utilities for handling carousels
 */

/**
 * Detects if an item is a carousel
 * @param {Object} item - The item to check
 * @returns {boolean} - true if it is a carousel, false otherwise
 */
export const isCarouselItem = (item) => {
  if (!item) return false;

  // Check new structure: type === "carousel" with array of urls
  if (
    item.type === "carousel" &&
    Array.isArray(item.urls) &&
    item.urls.length > 1
  ) {
    return true;
  }

  // Check gallery structure: isCarousel with carouselImages
  if (
    item.isCarousel === true &&
    Array.isArray(item.carouselImages) &&
    item.carouselImages.length > 1
  ) {
    return true;
  }

  return false;
};

/**
 * Extracts all URLs from a carousel
 * @param {Object} item - The item carousel
 * @returns {Array<string>} - Array of URLs from the carousel
 */
export const getCarouselUrls = (item) => {
  if (!isCarouselItem(item)) {
    return [];
  }

  // New structure: type === "carousel" with urls
  if (item.type === "carousel" && Array.isArray(item.urls)) {
    return item.urls.filter(Boolean);
  }

  // Gallery structure: carouselImages
  if (Array.isArray(item.carouselImages)) {
    return item.carouselImages
      .map((img) => (typeof img === "string" ? img : img.url))
      .filter(Boolean);
  }

  return [];
};

/**
 * Normalizes a carousel item to a standard structure
 * @param {Object} item - The item to normalize
 * @returns {Object|null} - Normalized item or null if it is not a carousel
 */
export const normalizeCarouselItem = (item) => {
  if (!isCarouselItem(item)) {
    return null;
  }

  const urls = getCarouselUrls(item);

  if (urls.length === 0) {
    return null;
  }

  // Normalize carouselImages to standard structure
  const carouselImages = urls.map((url, index) => ({
    url: url,
    id: `${item.id || "carousel"}-${index}`,
    rating: null,
    mediaData: item.mediaData || {},
  }));

  return {
    ...item,
    isCarousel: true,
    carouselImages: carouselImages,
    url: carouselImages[0]?.url || urls[0], // The main URL is the first one
  };
};

/**
 * Gets the number of elements in a carousel
 * @param {Object} item - The item carousel
 * @returns {number} - Number of elements (0 if it is not a carousel)
 */
export const getCarouselLength = (item) => {
  if (!isCarouselItem(item)) {
    return 0;
  }

  const urls = getCarouselUrls(item);
  return urls.length;
};
